import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { armazenamento } from './armazenamento.ts';
import { useAcessos } from './acessos.ts';

/**
 * A identidade presumida (ADR 0009, decisão 4).
 *
 * O que está aqui é o que se desenha de imediato: nome e email em
 * `localStorage`, pintados sem esperar por rede nenhuma. **O cookie é que
 * manda** — a sonda corrige isto quando o servidor responder. Offline, a
 * identidade presumida é tudo o que há, e chega.
 *
 * Nenhum testemunho vive aqui: a sessão é um cookie `HttpOnly` que o
 * JavaScript nunca vê. Perder este registo não termina sessão nenhuma —
 * só faz a interface voltar a perguntar ao servidor quem é.
 */

export interface Identidade {
  userId: string;
  /** Até o perfil chegar (bloco 4), é a parte do email antes do @. */
  nome: string;
  email: string;
  emailConfirmado: boolean;
  /**
   * Conta de revisão: abre todas as lições sem cumprir os pré-requisitos.
   *
   * Quem manda é o servidor — vem de `/api/eu` e da abertura de sessão. Fica
   * aqui, no registo persistido, pela mesma razão que o nome: para o catálogo
   * se desenhar destrancado sem esperar por rede nenhuma. Escrever `true` à
   * mão neste registo não abre lição nenhuma que o servidor não abra também,
   * porque o desbloqueio serve para **ver** o conteúdo e o progresso continua
   * a ser o que o dispositivo produziu.
   *
   * Não é pagamento. Ver a migração 20260809000100 e `content/estado.ts`.
   */
  acessoTotal: boolean;
  /**
   * H-204 — dispensado do limite diário (subscrição ou inscrição activa,
   * decidido pelo servidor num sítio só). A contagem continua desligada
   * (ADR 0010); o campo persiste para o dia em que ligar não obrigar a
   * esperar por rede. Ausente em registos antigos: a leitura é por selector
   * e a ausência vale `false`.
   */
  dispensadoDeLimite?: boolean;
  /** H-170 — tem turmas atribuídas; liga a entrada «Turmas» na barra. */
  eTutor?: boolean;
  eCoordenador?: boolean;
  /**
   * ADR 0028 — mensagens por ler, o número do ícone da barra. Vem de
   * `/api/eu` a cada sonda; persiste para o ícone se desenhar sem rede.
   */
  mensagensPorLer?: number;
}

interface EstadoDeIdentidade {
  identidade: Identidade | null;
  definir: (identidade: Identidade) => void;
  definirMensagensPorLer: (n: number) => void;
  limpar: () => void;
}

export const useIdentidade = create<EstadoDeIdentidade>()(
  persist(
    (set) => ({
      identidade: null,
      definir: (identidade) => set({ identidade }),
      definirMensagensPorLer: (n) =>
        set((estado) => (estado.identidade ? { identidade: { ...estado.identidade, mensagensPorLer: n } } : {})),
      limpar: () => {
        // Os acessos são da conta: saem com ela (H-304).
        useAcessos.getState().limpar();
        set({ identidade: null });
      },
    }),
    { name: 'identidade', storage: armazenamento() },
  ),
);

/** ADR 0028 — a ausência vale zero, como nos outros campos opcionais. */
export function mensagensPorLer(estado: EstadoDeIdentidade): number {
  return estado.identidade?.mensagensPorLer ?? 0;
}

export function nomeDe(email: string): string {
  return email.split('@')[0] ?? email;
}

/**
 * Selector, e não uma leitura directa de `identidade.acessoTotal`: os registos
 * gravados antes de este campo existir não o têm, e `undefined` num sítio que
 * decide bloqueio é uma pergunta sem resposta. Aqui a ausência vale `false`,
 * que é a resposta segura.
 */
export function temAcessoTotal(estado: EstadoDeIdentidade): boolean {
  return estado.identidade?.acessoTotal === true;
}

/** H-204 — o mesmo padrão de selector, pela mesma razão. */
export function estaDispensadoDeLimite(estado: EstadoDeIdentidade): boolean {
  return estado.identidade?.dispensadoDeLimite === true;
}

/**
 * A sonda de sessão. Corre depois da primeira pintura, nunca antes: um
 * `await` disto no arranque custava RNF-002 inteiro em 3G lenta.
 *
 * Sem rede, falha em silêncio e a identidade presumida fica como está —
 * é exactamente o comportamento desenhado.
 */
export async function sondarSessao(): Promise<void> {
  try {
    const resposta = await fetch('/api/eu', { credentials: 'same-origin' });
    if (resposta.status === 401) {
      useIdentidade.getState().limpar();
      return;
    }
    if (!resposta.ok) return;
    const dados = (await resposta.json()) as {
      userId: string;
      email: string;
      emailConfirmado: boolean;
      acessoTotal?: boolean;
      dispensadoDeLimite?: boolean;
      eTutor?: boolean;
      eCoordenador?: boolean;
      mensagensPorLer?: number;
    };
    useIdentidade.getState().definir({
      userId: dados.userId,
      email: dados.email,
      emailConfirmado: dados.emailConfirmado,
      // Opcional na leitura porque um servidor mais antigo do que este pacote
      // não envia o campo — e nesse caso a resposta é a mesma que a de quem
      // não tem a conta.
      acessoTotal: dados.acessoTotal === true,
      dispensadoDeLimite: dados.dispensadoDeLimite === true,
      eTutor: dados.eTutor === true,
      eCoordenador: dados.eCoordenador === true,
      mensagensPorLer: typeof dados.mensagensPorLer === 'number' ? dados.mensagensPorLer : 0,
      nome: useIdentidade.getState().identidade?.nome ?? nomeDe(dados.email),
    });
  } catch {
    // Offline. A identidade presumida é a resposta.
  }
}
